import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { PasswordValidationComponent } from '../../components/password-validation/password-validation.component';
import { InputComponent } from '../../components/input/input.component';
import { VehicleFormFieldsComponent } from '../../components/vehicle-form-fields/vehicle-form-fields.component';
import { buildVehicleForm, vehicleFormValue } from '../../components/vehicle-form-fields/vehicle-form';
import { setContainerTypeAction } from '../../ngrx/actions/global.actions';
import { AlertService } from '../../services/alert.service';
import { DropOffService } from '../../services/drop-off.service';

/**
 * Public (unauthenticated) QR-code target: customers check in their vehicle at
 * drop-off. Phone is required, email optional; submission opens a draft
 * invoice in the mechanic's review queue.
 */
@Component({
  selector: 'app-vehicle-drop-off',
  templateUrl: './vehicle-drop-off.component.html',
  styleUrls: ['./vehicle-drop-off.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, InputComponent, VehicleFormFieldsComponent]
})
export class VehicleDropOffComponent extends PasswordValidationComponent implements OnInit {
  isSubmitting: boolean = false;
  submittedInvoiceNumber: number | null = null;
  // Set from the response: a password was submitted but the contact info
  // matched an existing account, so the password was discarded.
  accountAlreadyExists: boolean = false;

  contactForm: FormGroup = this.createContactForm();
  vehicleForm: FormGroup = buildVehicleForm();

  constructor(
    private dropOffService: DropOffService,
    private alertService: AlertService) {
    super();
  }

  ngOnInit() {
    this.dispatch(setContainerTypeAction({ containerType: 'normal' }));
  }

  private createContactForm(): FormGroup {
    const form = new FormGroup({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      phone: new FormControl('', [Validators.required, Validators.pattern(/^[\d\s().+-]{7,20}$/)]),
      email: new FormControl('', [Validators.email]),
      // Only meaningful when an email is given; matches the account default (on).
      emailNotifications: new FormControl<'yes' | 'no'>('yes'),
      createAccount: new FormControl<boolean>(false),
      // Only checked on submit when createAccount is ticked (see onSubmit).
      password: new FormControl('', [Validators.required, Validators.minLength(8), this.passwordValidation.bind(this)]),
      confirmPassword: new FormControl('', [Validators.required, this.passwordMatchValidation.bind(this)]),
      issueDescription: new FormControl('', [Validators.required, Validators.maxLength(1000)]),
    });
    // Editing the password must revalidate the confirm field.
    this.subs.push(form.get('password')!.valueChanges.subscribe(
      () => form.get('confirmPassword')?.updateValueAndValidity()
    ));
    return form;
  }

  passwordMatchValidation(formControl: FormControl): ValidationErrors | null {
    return formControl.value === this.contactForm?.get('password')?.value
      ? null
      : { passwordMatch: 'Passwords do not match!' };
  }

  get hasEmail(): boolean {
    return !!this.contactForm.get('email')?.value?.trim();
  }

  get wantsAccount(): boolean {
    return this.contactForm.get('createAccount')?.value === true;
  }

  onSubmit() {
    this.contactForm.markAllAsTouched();
    this.vehicleForm.markAllAsTouched();

    if (!this.contactForm.get('firstName')?.valid || !this.contactForm.get('lastName')?.valid) {
      this.alertService.showSnackBar('First and last name are required.');
      return;
    }
    if (!this.contactForm.get('phone')?.valid) {
      this.alertService.showSnackBar('Please enter a valid phone number.');
      return;
    }
    if (!this.contactForm.get('email')?.valid) {
      this.alertService.showSnackBar('Please enter a valid email address, or leave it blank.');
      return;
    }
    if (this.wantsAccount) {
      if (!this.hasEmail) {
        this.contactForm.get('email')?.markAsDirty();
        this.alertService.showSnackBar('Please enter an email address to create an account.');
        return;
      }
      if (!this.contactForm.get('password')?.valid) {
        this.alertService.showSnackBar('Please choose a password: at least 8 characters with upper and lower case letters, a number, and a special character.');
        return;
      }
      if (!this.contactForm.get('confirmPassword')?.valid) {
        this.alertService.showSnackBar('Passwords do not match.');
        return;
      }
    }
    if (!this.contactForm.get('issueDescription')?.valid) {
      this.alertService.showSnackBar('Please describe why you are dropping off your vehicle.');
      return;
    }
    if (!this.vehicleForm.valid) {
      this.alertService.showSnackBar('Please complete the vehicle information.');
      return;
    }

    this.isSubmitting = true;

    const email = this.contactForm.get('email')!.value?.trim() || undefined;

    this.subs.push(this.dropOffService.submitDropOff({
      firstName: this.contactForm.get('firstName')!.value.trim(),
      lastName: this.contactForm.get('lastName')!.value.trim(),
      phoneNumber: this.contactForm.get('phone')!.value.trim(),
      email,
      // The opt-in choice only exists when an email was given.
      emailNotificationsEnabled: email
        ? this.contactForm.get('emailNotifications')!.value === 'yes'
        : undefined,
      // Guarded above: wantsAccount without an email never reaches this point.
      password: this.wantsAccount ? this.contactForm.get('password')!.value : undefined,
      ...vehicleFormValue(this.vehicleForm),
      issueDescription: this.contactForm.get('issueDescription')!.value.trim(),
    }).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.submittedInvoiceNumber = response.invoiceNumber;
        this.accountAlreadyExists = response.accountAlreadyExists === true;
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        if (error.status === 429) {
          this.alertService.showSnackBar('Too many check-ins from this device. Please wait a few minutes and try again.');
        } else {
          this.alertService.showSnackBar('Failed to check in your vehicle. Please try again or see the front desk.');
        }
      }
    }));
  }

  checkInAnotherVehicle() {
    this.submittedInvoiceNumber = null;
    this.accountAlreadyExists = false;
    this.contactForm = this.createContactForm();
    this.vehicleForm = buildVehicleForm();
  }
}
